import { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { format, getMonth } from "date-fns";
import { ro } from "date-fns/locale";
import DatePicker, { registerLocale } from "react-datepicker";

import { createRCAApi } from "@lib/api";
import { addAlert } from "@store/app/actions";
import { makeApiCall } from "@lib/api/helpers";
import Button from "@components/Button";
import Heading from "@components/Heading";
import Arrow from "@components/Icons/Arrow";

registerLocale("ro", ro);

const toInt = (val) => {
  const num = Number(val);
  return isNaN(num) ? 0 : num;
};

const RCADataPolita = ({
  formData,
  addAlert,
  onFormDataUpdate,
  onApiDataUpdate,
  onStepChange,
  onLoadingStateChange,
}) => {
  useEffect(() => {
    if (!formData.startDate) {
      // Set default selected date to tomorrow at midnight
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      onFormDataUpdate({
        startDate: format(tomorrow, "yyyy-MM-dd'T'00:00:00"),
      });
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.startDate) {
      addAlert({
        message: "Va rugam selectati o data de incepere a politei.",
        variant: "error",
      });
      return;
    }

    onLoadingStateChange(true);

    const {
      vehicleSubcategoryId: oldSubCatId,
      firstRegistration,
      vignetteExpiration,
      itpExpiration,
      ...vehiclePayload
    } = formData.vehicle;

    const payload = {
      ...formData,
      vehicle: {
        ...vehiclePayload,
        vehicleCategoryId: toInt(vehiclePayload.vehicleCategoryId),
        vehicleRegistrationStatus: toInt(
          vehiclePayload.vehicleRegistrationStatus
        ),
        activityTypeId: toInt(vehiclePayload.activityTypeId),
        vehicleSubCategoryId: toInt(
          vehiclePayload.vehicleSubCategoryId || oldSubCatId
        ),
        ...(firstRegistration && {
          firstRegistration: format(
            new Date(firstRegistration),
            "yyyy-MM-dd'T'00:00:00"
          ),
        }),
        ...(vignetteExpiration && {
          vignetteExpiration: format(
            new Date(vignetteExpiration),
            "yyyy-MM-dd'T'00:00:00"
          ),
        }),
        ...(itpExpiration && {
          itpExpiration: format(
            new Date(itpExpiration),
            "yyyy-MM-dd'T'00:00:00"
          ),
        }),
        makeId: toInt(vehiclePayload.makeId),
        fuelTypeId: toInt(vehiclePayload.fuelTypeId),
        productionYear: toInt(vehiclePayload.productionYear),
        engineCapacity: toInt(vehiclePayload.engineCapacity),
        maxWeight: toInt(vehiclePayload.maxWeight),
        enginePowerKw: toInt(vehiclePayload.enginePowerKw),
        seatsNumber: toInt(vehiclePayload.seatsNumber),
        graweKilometraj: toInt(vehiclePayload.graweKilometraj),
        registrationTypeId: toInt(vehiclePayload.vehicleRegistrationStatus),
      },
    };

    const response = await makeApiCall({
      apiCall: createRCAApi,
      apiArgs: [payload],
      returnResponse: true,
      formatResponse: (response) => response.data.data,
      preFail: (response) => {
        addAlert({
          message:
            response?.data?.message ||
            "A aparut o eroare la crearea comenzii RCA.",
          variant: "error",
        });
      },
    });

    if (response?.id) {
      onApiDataUpdate({ orderId: response.id });
      onStepChange(5);
    }

    onLoadingStateChange(false);
  };

  const availableDays = useMemo(() => {
    const result = [];
    const start = new Date();
    start.setDate(start.getDate() + 1); // start from tomorrow

    for (let i = 0; i < 60; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      result.push(date);
    }

    return result;
  }, []);

  const handleDateChange = (date) => {
    onFormDataUpdate({
      startDate: format(new Date(date), "yyyy-MM-dd'T'00:00:00"),
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-[20px]">
        <Heading
          as="h4"
          display="h2"
          sameSizeOnMobile
          title="Data de incepere a valabilitatii politei"
        />

        <div className="bg-white w-full sm:py-[16px] lg:py-0">
          <div className="w-full sm:w-[80%] md:w-[70%] lg:w-full mx-auto">
            <DatePicker
              locale="ro"
              includeDates={availableDays}
              inline
              selectedDate={formData.startDate}
              onChange={(date) => handleDateChange(date)}
              renderCustomHeader={({
                date,
                decreaseMonth,
                increaseMonth,
                prevMonthButtonDisabled,
                nextMonthButtonDisabled,
              }) => (
                <div className="flex justify-between items-center px-[16px] py-[8px] bg-white">
                  <button
                    className={`w-6 h-6 center flex justify-center items-center rotate-180 ${
                      prevMonthButtonDisabled ? "opacity-0" : ""
                    }`}
                    type="button"
                    onClick={decreaseMonth}
                    disabled={prevMonthButtonDisabled}
                  >
                    <Arrow fill="#1D4ED8" width="100%" height="100%" />
                  </button>

                  <span className="capitalize text-[20px]">
                    {ro.localize.month(getMonth(date))}
                  </span>

                  <button
                    className={`w-6 h-6 center flex justify-center items-center ${
                      nextMonthButtonDisabled ? "opacity-0" : ""
                    }`}
                    type="button"
                    onClick={increaseMonth}
                    disabled={nextMonthButtonDisabled}
                  >
                    <Arrow fill="#1D4ED8" width="100%" height="100%" />
                  </button>
                </div>
              )}
            />

            <style jsx global>{`
              .react-datepicker__day-names {
                display: grid;
                grid-template-columns: repeat(7, minmax(0, 1fr));
                text-transform: uppercase;
                background-color: #eef9fe;
                text-align: center;
              }

              .react-datepicker__day-name {
                padding: 10px 20px;
                font-weight: 500;
              }

              .react-datepicker__month {
                background-color: white;
              }

              .react-datepicker__week {
                display: grid;
                grid-template-columns: repeat(7, minmax(0, 1fr));
              }

              .react-datepicker__day {
                padding: 10px;
                font-size: 16px;
                font-weight: 500;
                text-align: center;
                cursor: pointer;
              }

              .react-datepicker__day--today {
                background-color: #eef9fe;
              }

              .react-datepicker__day--disabled {
                color: #b3b3b3;
                pointer-events: none;
                cursor: default;
              }

              .react-datepicker__day--keyboard-selected {
                background-color: #ffcb05;
              }

              @media (min-width: 768px) {
                .react-datepicker__day {
                  padding: 20px;
                  font-size: 20px;
                }
              }
            `}</style>
          </div>
        </div>

        {formData.startDate && (
          <div className="text-[#808080] text-[14px] pb-[20px]">
            Data selectata:{" "}
            <span className="text-black font-medium capitalize">
              {format(new Date(formData.startDate), "dd MMMM", { locale: ro })}
            </span>
          </div>
        )}
      </div>

      {/* Summary Card */}
      <div className="bg-white pt-[20px] -mx-[15px] lg:-mx-[20px]">
        <div className="bg-[#F5FBF4] px-[15px] lg:px-[20px] pt-[20px]">
          <div className="bg-white p-[16px] flex flex-col lg:flex-row justify-between">
            <div className="text-sm">
              <p>
                <span className="text-[#808080]">Autovehicul:</span>{" "}
                <span className="font-medium">
                  {formData.vehicle?.plateNo} -{" "}
                  {formData.vehicle?.commercialName}
                </span>
              </p>

              <p>
                <span className="text-[#808080]">Proprietar:</span>{" "}
                <span className="font-medium">
                  {`${
                    formData.vehicle.vehicleOwner?.companyName ||
                    formData.vehicle.vehicleOwner?.lastName
                  } ${formData.vehicle.vehicleOwner?.firstName || ""}`}
                </span>
              </p>

              <p>
                <span className="text-[#808080]">Conducator auto:</span>{" "}
                <span className="font-medium">
                  {formData.vehicle.vehicleDriver?.lastName}{" "}
                  {formData.vehicle.vehicleDriver?.firstName}
                </span>
              </p>
            </div>

            <button
              type="button"
              className="text-red-brand underline hover:text-bleu-hover font-medium text-left lg:text-right max-w-fit mt-[20px] lg:mt-0"
              onClick={() => onStepChange(2)}
            >
              Modifica
            </button>
          </div>
        </div>
      </div>

      <div className="pt-[20px] mt-[20px] text-center -mx-[15px] lg:-mx-[20px] -mb-[20px] bg-white">
        <Button
          variant="secondary"
          type="submit"
          title="Pasul urmator"
          classesProp="min-w-[300px] text-[20px] font-medium"
        />
      </div>
    </form>
  );
};

export default connect(null, { addAlert })(RCADataPolita);
